import styles from './evaluation-email.module.scss';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { makeCourseRequest, useCourseGetRequestHook } from '../../../requestUtils';
import { Accordion, AccordionDetails, AccordionSummary, Froala, Icon, MenuItem, PrimaryButton, SecondaryButton, Select, SnackBarContext, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, } from '@frontend-workspace/react-shared-lib';
import { IEmailTemplateSurvey } from '../../../_models/email-template-survey';
import { useTableData } from '../../table-data';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface EvaluationEmailProps { }

export function EvaluationEmail(props: EvaluationEmailProps) {
  const { data: emailTemplatesSurveys }: { data: IEmailTemplateSurvey[] } = useCourseGetRequestHook("settings/emailTemplates/surveys", []);

  const { rows } = useTableData();
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [id, setId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [emailTemplate, setEmailTemplate] = useState<string>('');

  const handleIdChange = (event: ChangeEvent<HTMLSelectElement>) => setId(event.target.value);

  useEffect(() => {
    if (emailTemplatesSurveys) {
      setId(emailTemplatesSurveys[0]?.id);
      const selectedTemplate = emailTemplatesSurveys.find(e => e.id === id);
      if (selectedTemplate) {
        setEmailTemplate(selectedTemplate.emailTemplateHtml);
        setId(selectedTemplate.id);
        setName(selectedTemplate.name);
      }
    }
  }, [id, emailTemplatesSurveys]);

  const handleResetEmailTemplate = () => {
    const selectedTemplate = emailTemplatesSurveys.find(e => e.id === id);
    if (selectedTemplate) {
      const emailTemplateCopy = selectedTemplate.emailTemplateHtml;
      setEmailTemplate(emailTemplateCopy);
    }
  };

  const handleUpdateEmailTemplate = () => {
    const body = {
      id: id,
      name: name,
      emailTemplateHtml: emailTemplate
    }
    makeCourseRequest(`settings/emailTemplates/surveys/upsert`, 'POST', body).then(result => {
      setSnackbarMessage({ content: t('tableData.updatedText'), type: "info", });
    }).catch(() => setSnackbarMessage({ content: t('tableData.couldNotUpdateText'), type: "error" }));
  };

  return (<>
    <Row>
      <Col xl={3}>
        <div className='mb-3'>
          <Select
            label={t('standard.email')}
            displayEmpty
            fullWidth
            sx={{ minWidth: 300 }}
            name='serveyEmailTemplate'
            value={id || ''}
            onChange={handleIdChange}>
            {emailTemplatesSurveys.map(e => (
              <MenuItem
                key={e.id}
                value={e.id}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </Col>
    </Row>
    <Row>
      <Col xl={8}>
        <div className='mb-3'>
          <TextField
            label={t('evaluationEmail.nameOfTemplate')}
            name='name'
            fullWidth
            value={name || ''}
            disabled
          />
        </div>
        <div className='mb-3'>
          <Accordion>
            <AccordionSummary
              expandIcon={<Icon>expand_more</Icon>}>
              <div className='d-flex align-items-center'>
                <Icon className='me-1' color='info'>info_outline</Icon>{t('tableData.explanationsOfVariousCodes')}
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table sx={{ width: '100%' }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('standard.code')}</TableCell>
                      <TableCell>{t('standard.description')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow hover key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </div>
        <div className='mb-3'>
          <label className='mb-1'>{t('evaluationEmail.template')}</label>
          <Froala
            maxHeight={800}
            model={emailTemplate || ''}
            onModelChanged={setEmailTemplate}
          />
        </div>
        <div>
          <PrimaryButton
            variant="contained"
            onClick={handleUpdateEmailTemplate}
            className='minButtonWidth me-3'>
            {t('standard.save')}
          </PrimaryButton>
          <SecondaryButton
            variant='outlined'
            onClick={handleResetEmailTemplate}
            className='minButtonWidth'>
            {t('standard.reset')}
          </SecondaryButton>
        </div>
      </Col>
    </Row>
  </>);
}

export default EvaluationEmail;
